import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SlideOver, SlideOverFooter } from '@/components/ui/slide-over'
import { ErrorSummary, type ErrorSummaryItem } from '@/components/visitone/error-summary'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { useRegisterUnsavedChanges, useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { usePeopleMutations, useRoles } from '@/hooks/use-people'
import { PeopleRequestError, type Invitation } from '@/lib/bff/people'
import { RoleChoices, type RoleChoice } from '@/components/visitone/surfaces/people/people-shared'
import { builtInDescription, EMAIL_FORMAT, errorCode, errorStatus } from '@/lib/people/people-text'

/** Identity's limit on the name given with an invitation. */
const MAX_NAME_LENGTH = 200

type FieldKey = 'email' | 'name' | 'roles'
type Errors = Partial<Record<FieldKey, string>>

const FIELD_IDS: Record<FieldKey, string> = { email: 'invite-email', name: 'invite-name', roles: 'invite-roles' }

/*
 * Invite a person (VIS-332): a one-shot panel that sends an invitation. Only
 * roles the caller can give are offered. Closing it with anything entered
 * asks first (VIS-321), and a refusal or failure keeps every entry.
 */
export function InvitePersonPanel({
  open,
  memberEmails,
  onClose,
  onSent,
}: {
  open: boolean
  /** The loaded users' addresses, lower-cased, to refuse an existing user before sending. */
  memberEmails: Set<string>
  onClose: () => void
  onSent: (invitation: Invitation) => void
}) {
  const t = useT()
  const { tenantName: tenant } = useAccess()
  const { confirmThen } = useUnsavedChanges()
  const roles = useRoles(open)
  const mutations = usePeopleMutations()
  const summaryRef = React.useRef<HTMLDivElement>(null)
  const formId = React.useId()

  const [email, setEmail] = React.useState('')
  const [name, setName] = React.useState('')
  const [selected, setSelected] = React.useState<string[]>([])
  const [errors, setErrors] = React.useState<Errors>({})
  const [summaryShown, setSummaryShown] = React.useState(false)
  const [failure, setFailure] = React.useState<'failed' | 'refused' | null>(null)
  const [sending, setSending] = React.useState(false)

  const entered = (email.trim() ? 1 : 0) + (name.trim() ? 1 : 0) + selected.length
  useRegisterUnsavedChanges(
    open ? { count: entered, subject: t('people.invite.subject'), discard: () => undefined } : null,
  )

  const choices: RoleChoice[] = (roles.roles ?? [])
    .filter((role) => role.canAssign)
    .map((role) => ({ role, description: builtInDescription(t, role.id, tenant) }))

  const labels: Record<FieldKey, string> = {
    email: t('people.invite.email'),
    name: t('people.invite.name'),
    roles: t('people.invite.roles', { tenant }),
  }
  const summaryItems: ErrorSummaryItem[] = (Object.keys(FIELD_IDS) as FieldKey[]).flatMap((key) =>
    errors[key] ? [{ key, label: labels[key], message: errors[key] as string }] : [],
  )

  const showErrors = (next: Errors) => {
    setErrors(next)
    setSummaryShown(true)
    requestAnimationFrame(() => summaryRef.current?.focus())
  }

  const jumpTo = (key: string) => {
    const target =
      key === 'roles'
        ? document.querySelector<HTMLElement>(`#${FIELD_IDS.roles} input`)
        : document.getElementById(FIELD_IDS[key as FieldKey])
    target?.focus()
  }

  const clearError = (key: FieldKey) => {
    setErrors((current) => ({ ...current, [key]: undefined }))
    setFailure(null)
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (sending) return
    const value = email.trim()
    const given = name.trim()
    const next: Errors = {}
    if (!value) next.email = t('people.invite.emailRequired')
    else if (!EMAIL_FORMAT.test(value)) next.email = t('people.invite.emailFormat')
    else if (memberEmails.has(value.toLowerCase())) next.email = t('people.invite.alreadyUser', { email: value, tenant })
    if (given.length > MAX_NAME_LENGTH) next.name = t('people.invite.nameTooLong')
    if (selected.length === 0) next.roles = t('people.invite.rolesRequired')
    if (Object.keys(next).length > 0) return showErrors(next)

    setFailure(null)
    setSending(true)
    try {
      const invitation = await mutations.invite({ email: value, ...(given ? { name: given } : {}), roles: selected })
      setEmail('')
      setName('')
      setSelected([])
      onSent(invitation)
    } catch (error) {
      const fromServer = serverErrors(error, value, tenant, t)
      if (fromServer) showErrors(fromServer)
      else setFailure(errorStatus(error) === 403 ? 'refused' : 'failed')
    } finally {
      setSending(false)
    }
  }

  const toggle = (roleId: string, next: boolean) => {
    setSelected((current) => (next ? [...current, roleId] : current.filter((id) => id !== roleId)))
    clearError('roles')
  }

  return (
    <SlideOver
      open={open}
      title={t('people.invite.title')}
      onClose={onClose}
      footer={
        <SlideOverFooter>
          <Button variant="secondary" size="lg" onClick={() => confirmThen({ kind: 'close' }, onClose)}>
            {t('people.invite.cancel')}
          </Button>
          <Button variant="primary" size="lg" type="submit" form={formId} aria-disabled={sending || undefined}>
            {sending ? t('people.invite.sending') : t('people.invite.submit')}
          </Button>
        </SlideOverFooter>
      }
    >
      <form id={formId} noValidate onSubmit={submit} className="flex flex-col gap-5">
        <p className="text-sm text-fg-muted">{t('people.invite.body', { tenant })}</p>
        {summaryShown && summaryItems.length > 0 ? (
          <ErrorSummary
            ref={summaryRef}
            title={t('people.invite.errorSummaryTitle', { count: summaryItems.length })}
            errors={summaryItems}
            onJump={jumpTo}
          />
        ) : null}
        {failure === 'failed' ? (
          <OperationFeedback state="error" title={t('people.invite.failedTitle')} detail={t('people.invite.failedBody')} />
        ) : null}
        {failure === 'refused' ? (
          <OperationFeedback
            state="error"
            title={t('core.access.actionRefusedTitle')}
            detail={t('core.access.actionRefusedBody', { tenant })}
          />
        ) : null}
        <Field id={FIELD_IDS.email} label={labels.email} help={t('people.invite.emailHelp')} required error={errors.email}>
          <Input
            type="email"
            autoComplete="off"
            aria-required="true"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              clearError('email')
            }}
          />
        </Field>
        <Field id={FIELD_IDS.name} label={labels.name} help={t('people.invite.nameHelp')} error={errors.name}>
          <Input
            autoComplete="off"
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              clearError('name')
            }}
          />
        </Field>
        {roles.error ? (
          <OperationFeedback
            state="error"
            title={t('people.rolesPage.loadFailed.title')}
            detail={t('people.rolesPage.loadFailed.body')}
            onRetry={() => void roles.revalidate()}
          />
        ) : !roles.roles ? (
          <p role="status" className="text-sm text-fg-muted">
            {t('people.rolesPage.loading')}
          </p>
        ) : (
          <RoleChoices
            id={FIELD_IDS.roles}
            legend={labels.roles}
            help={t('people.invite.rolesHelp')}
            error={errors.roles}
            choices={choices}
            selected={selected}
            onToggle={toggle}
          />
        )}
      </form>
    </SlideOver>
  )
}

/** The field errors for a refusal the server explains, or null for a failure to show as a whole. */
function serverErrors(error: unknown, email: string, tenant: string, t: ReturnType<typeof useT>): Errors | null {
  const code = errorCode(error)
  if (code === 'already_member') return { email: t('people.invite.alreadyUser', { email, tenant }) }
  if (code === 'invitation_exists') return { email: t('people.invite.alreadyInvited', { email }) }
  if (code === 'landlord_email') return { email: t('people.invite.landlord', { email, tenant }) }
  if (code === 'beyond_grant_limit') return { roles: t('people.invite.rolesUnavailable') }
  if (!(error instanceof PeopleRequestError) || error.problems.length === 0) return null
  const next: Errors = {}
  for (const problem of error.problems) {
    if (problem.field === 'email') {
      next.email = problem.code === 'required' ? t('people.invite.emailRequired') : t('people.invite.emailFormat')
    } else if (problem.field === 'name') {
      next.name = t('people.invite.nameTooLong')
    } else if (problem.field === 'roles') {
      next.roles = problem.code === 'required' ? t('people.invite.rolesRequired') : t('people.invite.rolesUnavailable')
    }
  }
  return Object.keys(next).length > 0 ? next : null
}
